import { rtLogService } from '@rammetec/core'
import type { WsClient, WsStatus } from '@rammetec/core/ws-client'
import { connectSystemApi, globalConfigService } from '@syscolect/application'
import { env } from '@/config/env'
import { authService } from './authService'
import { ensureConsoleSession, getConsoleSessionInfo } from './console-session.bootstrap'

let client: WsClient | null = null
let status: WsStatus = 'CLOSED'
let lastError: string | null = null
let unsubscribeStatus: (() => void) | null = null
let connectPromise: Promise<void> | null = null
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

function setStatus(next: WsStatus, error: string | null = null): void {
  status = next
  lastError = error
  emit()
}

async function openConnection(): Promise<WsClient> {
  await ensureConsoleSession()

  const globalConfig = globalConfigService.getConfig()
  const systemApiHost = env.systemApiWsHost ?? globalConfig?.systemApiHostWs

  if (!systemApiHost) {
    throw new Error('Host WebSocket da System API não configurado.')
  }

  const { sessionId, sessionHash } = getConsoleSessionInfo()
  const next = connectSystemApi({
    host: systemApiHost,
    getToken: async () => (await authService.getIdToken()) ?? '',
    sessionId,
    sessionHash,
    platform: 'web',
    isDev: env.appEnvironment === 'development',
  })

  unsubscribeStatus = next.onStatusChange((nextStatus) => setStatus(nextStatus))
  setStatus(next.getStatus())
  return next
}

export const systemApiWsStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
  getClient(): WsClient | null {
    return client
  },
  getStatus(): WsStatus {
    return status
  },
  getError(): string | null {
    return lastError
  },
  async connect(): Promise<void> {
    if (client || connectPromise) return connectPromise ?? Promise.resolve()

    setStatus('CONNECTING')
    connectPromise = openConnection()
      .then((next) => {
        client = next
        emit()
      })
      .catch((error) => {
        rtLogService.error('[system-api-ws.bootstrap] connect failed', { error })
        setStatus('CLOSED', error instanceof Error ? error.message : 'Falha ao conectar à System API.')
      })
      .finally(() => {
        connectPromise = null
      })

    return connectPromise
  },
  disconnect(): void {
    unsubscribeStatus?.()
    unsubscribeStatus = null
    client?.close()
    client = null
    setStatus('CLOSED')
  },
}
